// add-variant.component.ts
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { VariantService } from 'app/services/variant/variant.service';

@Component({
  selector: 'app-add-variant',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './add-variant.component.html',
  styleUrls: ['./add-variant.component.scss']
})
export class AddVariantComponent implements OnInit {
  variantForm!: FormGroup;
  submitted = false;
  isLoading = false;
  loadingMessage = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private variantService: VariantService
  ) {}

  ngOnInit() {
    this.initializeForm();
  }

  private initializeForm() {
    this.variantForm = this.fb.group({
      code: ['', [
        Validators.required, 
        Validators.minLength(2), 
        Validators.maxLength(20),
        Validators.pattern('[0-9]')
      ]],
      name: ['', [
        Validators.required, 
        Validators.minLength(2),
        Validators.maxLength(100)
      ]],
      measurement: ['', [Validators.required]],
      status: [true, [Validators.required]]
    });
  }

  // الدوال المساعدة للوصول إلى عناصر الفورم
  get f(): { [key: string]: AbstractControl } {
    return this.variantForm.controls;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.variantForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.variantForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        return 'هذا الحقل مطلوب';
      }
      if (field.errors['minlength']) {
        return `الحد الأدنى للحروف هو ${field.errors['minlength'].requiredLength}`;
      }
      if (field.errors['maxlength']) {
        return `الحد الأقصى للحروف هو ${field.errors['maxlength'].requiredLength}`;
      }
      if (field.errors['pattern']) {
        return 'يجب أن يحتوي الكود على أحرف إنجليزية وأرقام وشرطة سفلية فقط';
      }
    }
    return '';
  }

  // إنشاء المتغير
  onSubmit(): void {
    this.submitted = true;

    if (!this.variantForm.valid) {
      this.markFormGroupTouched(this.variantForm);
      alert('❌ يوجد أخطاء في النموذج. راجع الحقول المطلوبة.');
      return;
    }

    this.createVariant();
  }

  private createVariant(): void {
    this.isLoading = true;
    this.loadingMessage = 'جاري إنشاء المتغير...';

    const variantData = this.prepareVariantData();

    console.log('🟡 Creating variant with data:', variantData);

    this.variantService.createVariant(variantData).subscribe({
      next: (response) => {
        console.log('✅ Variant created successfully:', response);
        this.isLoading = false;
        
        if (response.success) {
          alert('✅ تم إنشاء المتغير بنجاح!');
          this.router.navigate(['/admin/variants']);
        } else {
          alert('❌ ' + (response.message || 'حدث خطأ أثناء إنشاء المتغير'));
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error creating variant:', error);
        this.isLoading = false;
        this.handleError(error);
      }
    });
  }

  private prepareVariantData(): any {
    const formValue = this.variantForm.value;
    
    return {
      code: formValue.code.trim(),
      name: formValue.name.trim(),
      measurement: formValue.measurement,
      status: Boolean(formValue.status)
    };
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

  private handleError(error: HttpErrorResponse): void {
    let errorMessage = '❌ حدث خطأ أثناء إنشاء المتغير';
    
    if (error.error && error.error.errors) {
      // معالجة أخطاء التحقق من الصحة من الخادم
      const validationErrors = error.error.errors;
      const errorMessages = [];
      
      for (const field in validationErrors) {
        if (validationErrors.hasOwnProperty(field)) {
          errorMessages.push(...validationErrors[field]);
        }
      }
      
      if (errorMessages.length > 0) {
        errorMessage = '❌ ' + errorMessages.join('\n');
      }
    } else if (error.error && error.error.message) {
      errorMessage = '❌ ' + error.error.message;
    } else if (error.status === 409) {
      errorMessage = '❌ كود المتغير موجود مسبقاً';
    }
    
    alert(errorMessage);
  }

  onReset(): void {
    if (confirm('هل تريد إعادة تعيين النموذج؟ سيتم فقدان جميع البيانات.')) {
      this.variantForm.reset({
        code: '',
        name: '',
        measurement: '',
        status: true
      });
      this.submitted = false;
    }
  }

  onCancel(): void {
    if (confirm('هل تريد إلغاء التغييرات؟')) {
      this.router.navigate(['/admin/variants']);
    }
  }

  // دالة للتحقق من صحة الكود
  validateCode(): void {
    const codeControl = this.variantForm.get('code');
    if (codeControl?.value) {
      const code = codeControl.value;
      if (!/[0-9]/.test(code)) {
        codeControl.setErrors({ pattern: true });
      } else {
        // إزالة خطأ النمط إذا كان الكود صالحاً
        if (codeControl.errors?.['pattern']) {
          delete codeControl.errors['pattern'];
          if (Object.keys(codeControl.errors).length === 0) {
            codeControl.setErrors(null);
          }
        }
      }
    }
  }
}